import { z } from "zod";

const resourceActionSchema = z.enum([
  "preview",
  "download",
  "chat",
  "rename",
  "delete",
]);

const cloudResourceWireSchema = z
  .object({
    available_actions: z.array(resourceActionSchema),
    created_at: z.string().datetime({ offset: true }),
    created_by_user_id: z.string().min(1),
    display_name: z.string().min(1).max(512),
    extension: z.string().max(32),
    mime_type: z.string().min(1).max(255),
    resource_id: z.string().min(1),
    resource_type: z.enum(["image", "video", "file"]),
    revision: z.number().int().positive(),
    size_bytes: z.number().int().nonnegative(),
    source_kind: z.string().min(1).max(120),
  })
  .strict();

function toCloudResource(
  resource: z.infer<typeof cloudResourceWireSchema>,
) {
  return {
    availableActions: resource.available_actions,
    createdAt: resource.created_at,
    createdByUserId: resource.created_by_user_id,
    displayName: resource.display_name,
    extension: resource.extension,
    mimeType: resource.mime_type,
    resourceId: resource.resource_id,
    resourceType: resource.resource_type,
    revision: resource.revision,
    sizeBytes: resource.size_bytes,
    sourceKind: resource.source_kind,
  };
}

export const myFilesListResponseSchema = z
  .object({
    items: z.array(cloudResourceWireSchema),
    next_cursor: z.string(),
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    items: response.items.map(toCloudResource),
    nextCursor: response.next_cursor,
    schemaVersion: response.schema_version,
  }));

const sourceSchema = z
  .object({
    conversation_id: z.string().min(1),
    excerpt: z.string(),
    message_id: z.string().min(1),
  })
  .strict()
  .nullable();

const referenceSchema = z
  .object({
    display_name: z.string().min(1),
    preview_kind: z.enum(["media", "unsupported"]),
    resource_id: z.string().min(1),
  })
  .strict();

export const myFilesDetailResponseSchema = z
  .object({
    resource: cloudResourceWireSchema.extend({
      dimensions: z
        .object({
          height: z.number().nonnegative().nullable(),
          width: z.number().nonnegative().nullable(),
        })
        .strict(),
      preview: z
        .object({ kind: z.enum(["media", "unsupported"]) })
        .strict(),
      references: z.array(referenceSchema),
      source: sourceSchema,
    }),
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => {
    const resource = response.resource;
    return {
      resource: {
        ...toCloudResource(resource),
        dimensions: resource.dimensions,
        preview: resource.preview,
        references: resource.references.map((reference) => ({
          displayName: reference.display_name,
          previewKind: reference.preview_kind,
          resourceId: reference.resource_id,
        })),
        source: resource.source
          ? {
              conversationId: resource.source.conversation_id,
              excerpt: resource.source.excerpt,
              messageId: resource.source.message_id,
            }
          : null,
      },
      schemaVersion: response.schema_version,
    };
  });

export const myFilesResourceMutationResponseSchema = z
  .object({
    resource: cloudResourceWireSchema,
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    resource: toCloudResource(response.resource),
    schemaVersion: response.schema_version,
  }));

export const myFilesAccessUrlResponseSchema = z
  .object({
    expires_at: z.string().datetime({ offset: true }),
    method: z.literal("GET"),
    resource_id: z.string().min(1),
    signed_headers: z.record(z.string(), z.string()),
    url: z.string().url(),
  })
  .strict()
  .transform((response) => ({
    expiresAt: response.expires_at,
    resourceId: response.resource_id,
    url: response.url,
  }));

const archiveWireSchema = z
  .object({
    archive_id: z.string().min(1),
    download_url: z.string().url().nullable(),
    expires_at: z.string().datetime({ offset: true }).nullable(),
    failure_code: z.string(),
    resource_count: z.number().int().positive().max(100),
    revision: z.number().int().positive(),
    size_bytes: z.number().int().nonnegative(),
    status: z.enum([
      "queued",
      "processing",
      "available",
      "failed",
      "expired",
    ]),
  })
  .strict();

export const myFilesArchiveResponseSchema = z
  .object({
    archive: archiveWireSchema,
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    archive: {
      archiveId: response.archive.archive_id,
      downloadUrl: response.archive.download_url,
      expiresAt: response.archive.expires_at,
      failureCode: response.archive.failure_code,
      resourceCount: response.archive.resource_count,
      revision: response.archive.revision,
      sizeBytes: response.archive.size_bytes,
      status: response.archive.status,
    },
    schemaVersion: response.schema_version,
  }));

export const myFilesConversationResponseSchema = z
  .object({
    conversation: z
      .object({
        id: z.string().min(1),
      })
      .passthrough(),
  })
  .strict()
  .transform((response) => ({ conversationId: response.conversation.id }));

export type MyFilesResource = ReturnType<typeof toCloudResource>;
export type MyFilesDetail = ReturnType<
  typeof myFilesDetailResponseSchema.parse
>["resource"];
export type MyFilesArchive = ReturnType<
  typeof myFilesArchiveResponseSchema.parse
>["archive"];
